import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../components/Config/api.service';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE, enveloppeDe } from '../Employee/organigrammeOutils';
import { CARTE, montant } from './departOutils';

/**
 * Le solde de tout compte d'un départ, calculé par la Paie (Yros/Kavero#61,
 * #62) : les indemnités, le total, et les prêts en cours au départ — dû,
 * retenu sur le dernier bulletin, reste à régler par chèque (Yros/Kavero#434).
 *
 * Tant que le départ est lancé (solde en brouillon), la RH corrige un montant
 * à la main, revient au calcul ou fait recalculer — par des boutons, jamais
 * de recalcul à la saisie. Validé, le solde se lit seulement.
 */
function DepartStc({ depart, stc, erreur, onStc }) {
  const { t } = useTranslation();
  const [edition, setEdition] = useState(null); // { champ, valeur }
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState('');
  const modifiable = depart.etat === 'lance';

  const appeler = async (appel) => {
    setEnCours(true);
    setMessage('');
    const reponse = await enveloppeDe(appel);
    setEnCours(false);
    if (reponse.ok) {
      onStc(reponse.stc);
      return true;
    }
    setMessage(reponse.message || t('departs.stc.erreur'));
    return false;
  };

  const enregistrer = async (event) => {
    event.preventDefault();
    const valeur = Number(edition.valeur);
    if (edition.valeur === '' || Number.isNaN(valeur) || valeur < 0) {
      setMessage(t('departs.stc.montantInvalide'));
      return;
    }
    if (await appeler(() => apiService.corrigerMontantsStc(depart.id, { [edition.champ]: valeur }))) setEdition(null);
  };

  return (
    <div className={CARTE} data-testid="depart-stc">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-base font-semibold">{t('departs.stc.titre')}</h2>
        {modifiable && stc && (
          <button type="button" data-testid="stc-recalculer" disabled={enCours} className={BOUTON_SECONDAIRE}
            onClick={() => appeler(() => apiService.recalculerStc(depart.id))}>
            {t('departs.stc.recalculer')}
          </button>
        )}
      </div>

      {erreur && <p data-testid="stc-indisponible" className="text-sm text-gray-600">{erreur}</p>}

      {stc && (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-medium">{t('departs.stc.indemnite')}</th>
                <th className="py-2 font-medium text-right">{t('departs.stc.montant', { devise: stc.devise })}</th>
                {modifiable && <th className="py-2" />}
              </tr>
            </thead>
            <tbody>
              {stc.indemnites.map((ligne) => (
                <tr key={ligne.champ} className="border-b last:border-0" data-testid={`stc-ligne-${ligne.champ}`}>
                  <td className="py-2">
                    {ligne.libelle}
                    {ligne.corrige && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700">
                        {t('departs.stc.corrige')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {edition?.champ === ligne.champ ? (
                      <form onSubmit={enregistrer} className="inline-flex gap-2 justify-end">
                        <input type="number" min="0" data-testid="stc-saisie" value={edition.valeur} autoFocus
                          onChange={(e) => setEdition({ ...edition, valeur: e.target.value })}
                          className="w-32 px-2 py-1 border rounded text-right" />
                        <button type="submit" data-testid="stc-enregistrer" disabled={enCours} className={BOUTON_PRINCIPAL}>
                          {t('departs.enregistrer')}
                        </button>
                        <button type="button" className={BOUTON_SECONDAIRE} onClick={() => setEdition(null)}>
                          {t('departs.annuler')}
                        </button>
                      </form>
                    ) : montant(ligne.montant)}
                  </td>
                  {modifiable && (
                    <td className="py-2 text-right whitespace-nowrap">
                      {edition?.champ !== ligne.champ && (
                        <button type="button" data-testid={`stc-corriger-${ligne.champ}`} disabled={enCours}
                          className={BOUTON_SECONDAIRE}
                          onClick={() => setEdition({ champ: ligne.champ, valeur: String(ligne.montant) })}>
                          {t('departs.stc.corriger')}
                        </button>
                      )}
                      {ligne.corrige && (
                        <button type="button" data-testid={`stc-revenir-${ligne.champ}`} disabled={enCours}
                          className={BOUTON_SECONDAIRE}
                          onClick={() => appeler(() => apiService.revenirAuCalculStc(depart.id, ligne.champ))}>
                          {t('departs.stc.revenir')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="py-2">{t('departs.stc.total')}</td>
                <td className="py-2 text-right tabular-nums" data-testid="stc-total">{montant(stc.total)}</td>
                {modifiable && <td />}
              </tr>
            </tfoot>
          </table>

          {stc.prets && stc.prets.du > 0 && (
            <div className="mt-4 rounded-lg border border-slate-200 p-3 text-sm" data-testid="stc-prets">
              <h3 className="font-medium mb-2">{t('departs.prets.titre')}</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <dt className="text-gray-500">{t('departs.prets.du')}</dt>
                  <dd className="tabular-nums">{montant(stc.prets.du)} {stc.devise}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('departs.prets.retenu')}</dt>
                  <dd className="tabular-nums" data-testid="stc-prets-retenu">
                    {stc.prets.retenu === null ? t('departs.prets.aDeterminer') : `${montant(stc.prets.retenu)} ${stc.devise}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('departs.prets.reliquat')}</dt>
                  <dd className="tabular-nums font-semibold" data-testid="stc-prets-reliquat">
                    {stc.prets.reliquat === null ? t('departs.prets.aDeterminer') : `${montant(stc.prets.reliquat)} ${stc.devise}`}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </>
      )}

      {message && <p role="alert" data-testid="stc-message" className="mt-2 text-sm text-red-700">{message}</p>}
    </div>
  );
}

export default DepartStc;
